import type { CommunityMessage } from '../services/communityService';
import type { OptimisticMessage } from '../contexts/CommunityContext';

export function isMessageSelectable(message: OptimisticMessage): boolean {
  if (message._optimistic || message._failed) return false;
  if (message.message_type === 'system_event') return false;
  if (message.message_type === 'deleted' || message.deleted) return false;
  return true;
}

export function isOwnMessage(message: CommunityMessage, clientId?: string): boolean {
  return !!clientId && message.sender_client_id === clientId;
}

export function isCopyableTextMessage(message: OptimisticMessage): boolean {
  if (!isMessageSelectable(message)) return false;
  if (message.message_type !== 'text') return false;
  return !!(message.content && message.content.trim());
}

export function hasDownloadableMedia(message: OptimisticMessage): boolean {
  if (!isMessageSelectable(message)) return false;
  return !!message.media?.blob_url;
}

export function getSelectedMessages(
  messages: OptimisticMessage[],
  selectedIds: Set<string>
): OptimisticMessage[] {
  const order = messages.filter((m) => selectedIds.has(m.community_message_id));
  return order;
}

export function canShowDelete(selected: OptimisticMessage[], clientId?: string): boolean {
  if (selected.length === 0) return false;
  return selected.every((m) => isOwnMessage(m, clientId));
}

export function canShowCopy(selected: OptimisticMessage[]): boolean {
  if (selected.length === 0) return false;
  return selected.every(isCopyableTextMessage);
}

export function canShowDownload(selected: OptimisticMessage[]): boolean {
  return selected.some(hasDownloadableMedia);
}

export function canShowReply(selected: OptimisticMessage[]): boolean {
  return selected.length === 1 && isMessageSelectable(selected[0]);
}

export function canShowPin(selected: OptimisticMessage[]): boolean {
  return selected.length === 1 && isMessageSelectable(selected[0]);
}

export function buildCopyText(selected: OptimisticMessage[]): string {
  return selected
    .map((m) => m.content?.trim())
    .filter(Boolean)
    .join('\n\n');
}
